// src/utils/sectionSummary.js
//
// What a CLOSED form section says about itself (KPR-581): the header of a
// folded section must still tell whether anything inside needs a person, so a
// required field is never hidden by a closed section.
//
//   required   how many required elements in the section hold no value yet
//   invalid    how many fields hold a value the form refuses (a save attempt
//              or the server said so) — a flag, not a count, in the header
//   filled     how many of the section's fields hold a value — "6 of 9 filled",
//              shown on the read view only
//   total      the section's fields as a person counts them: on the read view
//              a range (a start and an end) is ONE field; the form draws its
//              halves as two fields and passes them as two (KPR-626)
//
// Pure and derived on every render from the item's values: nothing here is
// stored or served, so a stale-save merge, a late layout and a concurrent edit
// all just read through. It counts only the fields it is handed, and the
// callers hand it only what this reader can see (an element hidden from them
// is filtered out before panels exist), so a closed header never leaks that a
// hidden field exists.
//
// "Required" is asked exactly as the save asks it — the vendored isRequiredFor
// (so a conditional rule that holds only while ANOTHER section's field says so
// is live), and the same skips getElementValidationErrors makes: a value the
// server assigns or a person is held to "see only" is never asked for, and a
// file element is not asked for while the collection takes no files.

import { isElementValueEmpty, isNotTheirsToChange } from './elements';
import { isRequiredFor } from './required';

const hasName = def => !!def?.name;

function requiredMissing(def, values, attachmentsOff) {
	if (!hasName(def)) return false;
	if (attachmentsOff && def.dataType === 'file') return false;
	if (isNotTheirsToChange(def)) return false;
	return isRequiredFor(def, values || {}) && isElementValueEmpty(def, values?.[def.name]);
}

/**
 * @param fields `[{ element, end? }]` — one per field in the section, `end`
 *   being a range's second element where the pair is read as one field (the
 *   read view); the form passes each half as a field of its own.
 * @param values the item's `elements` map.
 * @param errors `{ [elementName]: message }` — the form's validationErrors;
 *   the read view has none.
 */
export function sectionSummary(fields = [], values = {}, { errors = {}, attachmentsOff = false } = {}) {
	const out = { total: 0, filled: 0, required: 0, invalid: 0 };
	(fields || []).forEach(({ element, end = null } = {}) => {
		if (!hasName(element)) return;
		out.total += 1;
		const defs = [element, end].filter(hasName);
		if (defs.some(def => !isElementValueEmpty(def, values?.[def.name]))) out.filled += 1;
		let missing = false;
		let refused = false;
		defs.forEach(def => {
			if (requiredMissing(def, values, attachmentsOff)) {
				out.required += 1;
				missing = true;
			} else if (errors?.[def.name]) {
				refused = true;
			}
		});
		// A required blank is already the count; the flag is for a value that is
		// there and not accepted, so one field is never said twice.
		if (refused && !missing) out.invalid += 1;
	});
	return out;
}

export default sectionSummary;
